import React, { useEffect, useState } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import PortletBody from '../../../app/components/portlet/PortletBody';
import StyledButton from '../../../app/components/styledComponents/StyledButton';
import StyledColoredTag from '../../../app/components/styledComponents/StyledColoredTag';
import StyledToolTip from '../../../app/components/styledComponents/StyledToolTip';
import CalenderIcon from '../../../app/components/svgIcons/CalenderIcon';
import FacebookIcon from '../../../app/components/svgIcons/FacebookIcon';
import InstagramIcon from '../../../app/components/svgIcons/InstagramIcon';
import WarningIcon from '../../../app/components/svgIcons/WarningIcon';
import { StyledLabel } from '../../../assets/styledComponents/Global';
import theme from '../../../utils/themes';
import { getStatusColor } from '../../../utils/helper';
import { useRetrySocialMediaPostMutation } from '../CampainApiSlice';

const MAX_SOCIAL_MEDIA_RETRIES = 3;

const platformLabel = (platform) => {
  if (platform === 'FACEBOOK') return 'Facebook';
  if (platform === 'INSTAGRAM') return 'Instagram';
  return platform || '-';
};

const formatDateTime = (inputDate) => {
  const date = moment(inputDate);
  return moment(date).isValid() ? date.format('Do MMM YYYY, HH:mm') : '-';
};

const SocialMediaSummary = ({ socialMediaPost, campaginRefetch }) => {
  const [post, setPost] = useState(socialMediaPost);
  const [retrySocialMediaPost, { isLoading: retryLoading }] =
    useRetrySocialMediaPostMutation();

  useEffect(() => {
    setPost(socialMediaPost);
  }, [socialMediaPost]);

  if (!post) return null;

  const { id, platform, statusId, postUrl, errorMessage, publishedAt, retryCount } =
    post;
  const showRetry = Boolean(errorMessage) || statusId !== 28;
  const remainingRetries = Math.max(
    0,
    MAX_SOCIAL_MEDIA_RETRIES - (retryCount ?? 0),
  );

  const onRetry = async () => {
    if (!id) return;
    try {
      const res = await retrySocialMediaPost(id).unwrap();
      if (res?.data) {
        setPost((prev) => ({ ...prev, ...res.data }));
      }
      toast.success(res?.message || 'Social media post retry completed');
    } catch (err) {
      // Global rtkQueryErrorLogger already toasts the API error
    } finally {
      const result = await campaginRefetch?.();
      const updatedPost = result?.data?.[0]?.socialMediaPost;
      if (updatedPost) {
        setPost(updatedPost);
      }
    }
  };

  return (
    <PortletBody className="mt-3 w-100 p-5">
      <h3 className="pb-2">Social Media</h3>
      <div className="border border-muted rounded mt-2 p-2">
        <div className="d-flex justify-content-between align-items-center p-2 p-lg-3 template-item bg-white rounded mt-1">
          <div className="text-start d-flex">
            <div className="px-2">
              {platform === 'FACEBOOK' ? (
                <FacebookIcon size={48} />
              ) : platform === 'INSTAGRAM' ? (
                <InstagramIcon size={48} />
              ) : (
                <StyledBox>{platform?.[0] || 'S'}</StyledBox>
              )}
            </div>
            <div className="col-12 fw-bold">
              <div className="mb-0 d-flex align-items-center">
                <StyledLabel weight={500} size="16px">
                  {platformLabel(platform)}
                </StyledLabel>
                {statusId === 28 && (
                  <StyledColoredTag
                    className="rounded-pill"
                    variant={getStatusColor(statusId)}
                  >
                    Posted
                  </StyledColoredTag>
                )}
                {errorMessage && (
                  <StyledToolTip
                    title={errorMessage}
                    childrenClass={''}
                    placement={'bottom'}
                  >
                    <WarningIcon size={18} color={theme.danger} />
                  </StyledToolTip>
                )}
              </div>
              <div className="col-12 d-flex mt-1 align-items-center flex-wrap">
                {publishedAt && (
                  <span className="d-flex align-items-center">
                    <CalenderIcon
                      className="ms-2"
                      style={{ verticalAlign: 'text-top' }}
                    />
                    <StyledLabel
                      weight={500}
                      color={theme.textColor}
                      className="mx-1"
                    >
                      {formatDateTime(publishedAt)}
                    </StyledLabel>
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {postUrl && (
              <StyledButton
                variant="white"
                onClick={() => {
                  const newWindow = window.open(postUrl, '_blank', 'noopener,noreferrer');
                  if (newWindow) newWindow.opener = null;
                }}
              >
                View post
              </StyledButton>
            )}
            {showRetry && (
              <>
                <StyledLabel
                  weight={500}
                  color={theme.textColor}
                  className="me-1"
                >
                  Remaining retries: {remainingRetries}
                </StyledLabel>
                <StyledButton
                  variant="white"
                  onClick={onRetry}
                  loading={retryLoading}
                  disabled={retryLoading || !id || remainingRetries === 0}
                >
                  Retry
                </StyledButton>
              </>
            )}
          </div>
        </div>
      </div>
    </PortletBody>
  );
};

const StyledBox = styled.div`
  padding: 2px;
  border-radius: 6px;
  background: ${theme.primaryLight};
  width: 50px;
  height: 50px;
  font-size: 30px;
  font-weight: 700;
  text-align: center;
`;

export default SocialMediaSummary;
